import { router } from "@inertiajs/react"
import { useCallback } from "react"

import { brandContentPath } from "@/routes"
import type { GalleryFilters } from "@/types/content-gallery"

/**
 * OKS-39 — URL state mutator for the Brand Content Gallery.
 *
 * Mirrors `useMissionListUrl` (OKS-31) shape. Three deltas:
 *   1. Filter set: mission_id / creator_id / from / to (no q / sort —
 *      sort is fixed in V1 per PRD OQ-4 / TRD KD-9).
 *   2. Defaults are NULL (not "all" sentinels) — `null` filter values
 *      drop from the URL entirely. Backend re-resolves to the
 *      brand-scoped floor when params are absent.
 *   3. Inertia partial-reload `only:` set is locked with the BE
 *      controller's prop bag and the LQE UI contract spec.
 */
export const RELOAD_KEYS = [
  "submissions",
  "pagination",
  "filter_options",
  "applied_filters",
  "is_empty",
] as const

/**
 * Build the URL params object from the next filter / page state. Drops
 * `null` / undefined / empty values so the URL stays minimal. Exported
 * for direct unit testing — the URL round-trip is the canonical canary
 * against drift between FE and BE.
 */
export function buildGalleryParams(
  filters: GalleryFilters,
  page?: number,
): Record<string, string | number> {
  const params: Record<string, string | number> = {}

  if (filters.mission_id != null) params.mission_id = filters.mission_id
  if (filters.creator_id != null) params.creator_id = filters.creator_id
  if (filters.from) params.from = filters.from
  if (filters.to) params.to = filters.to
  if (typeof page === "number" && page > 1) params.page = page

  return params
}

interface UseGalleryUrlReturn {
  /** Push next filter + optional page state to the URL via partial reload. */
  push: (next: Partial<GalleryFilters>, page?: number) => void
  /** Set a single filter, resets page to 1 (PRD AC). */
  setFilter: <K extends keyof GalleryFilters>(
    key: K,
    value: GalleryFilters[K],
  ) => void
  /** Navigate to a different page, preserving filters. */
  setPage: (page: number) => void
  /** Reset every filter and page back to defaults. */
  clearAll: () => void
}

/**
 * Hook exposing typed setters that emit normalized URL params via
 * Inertia partial reload. Caller passes the current snapshot so deltas
 * compute without re-reading `usePage()` per render.
 */
export function useGalleryUrl(applied: GalleryFilters): UseGalleryUrlReturn {
  const push = useCallback(
    (next: Partial<GalleryFilters>, page?: number) => {
      const merged: GalleryFilters = { ...applied, ...next }
      const params = buildGalleryParams(merged, page)

      router.get(brandContentPath(), params, {
        preserveState: true,
        preserveScroll: true,
        only: [...RELOAD_KEYS],
      })
    },
    [applied],
  )

  const setFilter = useCallback(
    <K extends keyof GalleryFilters>(key: K, value: GalleryFilters[K]) => {
      // Setting a filter always resets to page 1 (PRD §5).
      push({ [key]: value } as Partial<GalleryFilters>, 1)
    },
    [push],
  )

  const setPage = useCallback(
    (page: number) => {
      push({}, page)
    },
    [push],
  )

  const clearAll = useCallback(() => {
    router.get(
      brandContentPath(),
      {},
      {
        preserveState: true,
        preserveScroll: true,
        only: [...RELOAD_KEYS],
      },
    )
  }, [])

  return { push, setFilter, setPage, clearAll }
}
